import { AppBar, IconButton, Slide, Toolbar } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import { makeStyles } from '@material-ui/core/styles'
import { TransitionProps } from '@material-ui/core/transitions'
import { Close as CloseIcon, Menu as MenuIcon } from '@material-ui/icons'
import { Color } from '@ts/css'
import React, { useCallback, useMemo, useState } from 'react'

import { useAnalytics } from '../../lib/analytics'
import { Actions, Categories, Labels } from '../../lib/analytics/events'
import hexToRgba from '../../lib/HexToRgba'
import { Theme } from '../../lib/theming/Theme'

export type HeaderStylesProps = Partial<{
  backgroundColor: Color
  height: string
  iconColor: Color
  textColor: Color
}>

const HeaderTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide ref={ref} direction="down" {...props} timeout={400} />
})

type Navigation = {
  menus?: React.ReactNode
  styles?: HeaderStylesProps
}

const useStyles = makeStyles<Theme, HeaderStylesProps>(theme => ({
  appBar: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  backDrop: {
    backgroundColor: 'transparent',
  },
  burgerIcon: ({ iconColor = theme.palette.primary.contrastText }) => ({
    color: iconColor,
  }),
  closeButton: ({ iconColor = theme.palette.primary.contrastText }) => ({
    color: iconColor,
  }),
  dialogContentRoot: {
    display: 'flex',
    flex: 'initial',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: 'auto 0',
    maxWidth: '1240px',
    [theme.breakpoints.down('sm')]: {
      padding: '0',
    },
  },
  dialogPaper: ({ backgroundColor = theme.palette.primary.main }) => ({
    alignItems: 'center',
    backgroundColor: hexToRgba(backgroundColor, 0.95),
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 1101,
  }),
  iconFontSizeLarge: {
    fontSize: '2rem',
  },
  toolbarGutters: {
    justifyContent: 'flex-end',
  },
}))

const Navigation = ({ menus, styles = {} }: Navigation) => {
  const [open, setOpen] = useState(false)
  const classes = useStyles(styles)
  const { trackEvent } = useAnalytics()

  const handleClickOpen = useCallback(() => {
    trackEvent({
      action: Actions.BurgerIconClick,
      category: Categories.HeaderMenu,
      label: Labels.Open,
      location: Categories.HeaderMenu,
    })
    setOpen(true)
  }, [trackEvent])

  const handleClose = useCallback(() => {
    trackEvent({
      action: Actions.BurgerIconClick,
      category: Categories.HeaderMenu,
      label: Labels.Close,
      location: Categories.HeaderMenu,
    })
    setOpen(false)
  }, [trackEvent])

  const fontSizeLarge = classes.iconFontSizeLarge
  const iconClasses = useMemo(() => ({ fontSizeLarge }), [fontSizeLarge])

  return menus ? (
    <>
      <IconButton
        aria-label="menu"
        classes={{ root: classes.burgerIcon }}
        edge="end"
        onClick={handleClickOpen}
      >
        <MenuIcon classes={iconClasses} fontSize="large" />
      </IconButton>
      <Dialog
        fullScreen
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
        TransitionComponent={HeaderTransition}
        classes={{ paper: classes.dialogPaper }}
        open={open}
        onClose={handleClose}
      >
        <AppBar classes={{ root: classes.appBar }} position="static">
          <Toolbar classes={{ gutters: classes.toolbarGutters }}>
            <IconButton
              aria-label="close"
              classes={{ root: classes.closeButton }}
              edge="end"
              onClick={handleClose}
            >
              <CloseIcon classes={iconClasses} fontSize="large" />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent classes={{ root: classes.dialogContentRoot }}>{menus}</DialogContent>
      </Dialog>
    </>
  ) : null
}

export default Navigation
